import React from 'react';
import { motion, useScroll, useTransform, Variants, easeOut } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

// --- Animation Variants (Refined for Dynamic Page) ---
const fadeIn: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: easeOut,
    },
  },
};

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1],
    },
  },
};

const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0.42, 0, 1, 1],
    },
  },
};

const slideInRight: Variants = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0.42, 0, 1, 1],
    },
  },
};

const SMMPage: React.FC = () => {
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null); // Assuming you might add another section or use this
  const ref5 = useRef(null);

  // Scroll effects for subtle parallax/fade
  const { scrollYProgress: scrollYProgress1 } = useScroll({ target: ref1, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress1, [0, 1], ["-10%", "10%"]);
  const opacity1 = useTransform(scrollYProgress1, [0, 0.5, 1], [0, 1, 0]);

  const { scrollYProgress: scrollYProgress2 } = useScroll({ target: ref2, offset: ["start end", "end start"] });
  const y2 = useTransform(scrollYProgress2, [0, 1], ["-10%", "10%"]);
  // Optional: Add opacity for ref2 background if desired
  const opacity2 = useTransform(scrollYProgress2, [0, 0.5, 1], [0, 1, 0.5]);


  const { scrollYProgress: scrollYProgress3 } = useScroll({ target: ref3, offset: ["start end", "end start"] });
  const scale3 = useTransform(scrollYProgress3, [0.1, 0.7], [0.8, 1]);
  const opacity3 = useTransform(scrollYProgress3, [0, 0.5, 1], [0, 1, 0.5]);

  return (
    <>
      {/* --- SEO Head Section (for Pages Router) --- */}
      {/* If using App Router, ensure the 'metadata' object is exported above and remove this <Head> component. */}
      {/* <Head> */}
        <title>Social Media Domination | Google Ads Expert | Nadezhda Venture </title>
        <meta
          name="description"
          content="Unleash your brand’s potential with innovative SMM strategies. [Your Company Name] delivers engagement, leads, and ROI through cutting-edge social media marketing in Noida & beyond."
        />
        <link rel="canonical" href="https://yourwebsite.com/social-media-marketing" />
        <meta property="og:title" content="Social Media Domination | [Your Company Name]" />
        <meta property="og:description" content="Transforming brands through data-driven and creative social media marketing." />
        <meta property="og:url" content="https://yourwebsite.com/social-media-marketing" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://yourwebsite.com/images/og-smm-dynamic.jpg" /> {/* Placeholder */}
        <meta property="og:image:alt" content="Dynamic Social Media Growth" />
      {/* </Head> */}

      <div className="smm-page min-h-screen bg-gradient-to-br from-white to-blue-50 text-gray-900 font-sans relative overflow-hidden">

        {/* Floating background elements for dynamic feel */}
        <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">
          <img src="/images/social-icon-blob-1.svg" alt="Social icon blob" className="absolute top-[10%] left-[5%] w-[150px] opacity-20 animate-float" />
          <img src="/images/social-icon-blob-2.svg" alt="Social icon blob" className="absolute bottom-[5%] right-[10%] w-[200px] opacity-20 animate-float-delay" />
          <img src="/images/social-icon-blob-3.svg" alt="Social icon blob" className="absolute top-[30%] right-[3%] w-[100px] opacity-20 animate-float-slow" />
        </div>

        {/* 1. Hero Section: Full-Screen Visual Impact */}
        <motion.section
          className="relative h-[450px] flex flex-col items-center justify-center text-center bg-black text-white p-6 overflow-hidden z-10"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="absolute inset-0 z-0 opacity-30">
            <img
              src="https://img1.wsimg.com/isteam/getty/2083917031/:/cr=t:1.96%25,l:0%25,w:100%25,h:96.08%25/rs=w:1240,h:620,cg:true"
              alt="Dynamic social network background"
              className="w-full h-full object-cover opacity-80" // Opacity applied directly to the image
              loading="eager"
            />
            {/* Dark overlay to enhance text readability over the image */}
            <div className="absolute inset-0  opacity-40"></div>
          </div>

          <div className="z-10 max-w-5xl mx-auto flex flex-col items-center">
            <motion.h1
              className="text-3xl md:text-5xl font-extrabold leading-none mb-6 drop-shadow-xl tracking-tighter text-orange-500"
              variants={scaleIn}
              transition={{ delay: 0.3 }}
            >
              IGNITE YOUR DIGITAL PRESENCE
            </motion.h1>
            <motion.p
              className="text-2xl md:text-2xl mb-12 font-light opacity-90 max-w-3xl"
              variants={fadeIn}
              transition={{ delay: 0.8 }}
            >
              We transform your brand's social media into a powerhouse of engagement, leads, and measurable growth.
            </motion.p>
            <motion.button
              // className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-full shadow-2xl transform transition duration-300 ease-in-out hover:scale-105 text-xl flex items-center justify-center animate-bounce-slow"
              variants={fadeIn}
              transition={{ delay: 1.2 }}
              whileHover={{ scale: 1.05, boxShadow: '0 12px 30px rgba(0,0,0,0.4)' }}
              whileTap={{ scale: 0.95 }}
              aria-label="Start your social media journey"
            >
              <i className="fas fa-rocket mr-4"></i> 
             <Link to="/contact">
  <button className="bg-orange-700 hover:bg-orange-500 px-6 py-3 rounded-lg font-semibold transition text-white">
     Launch Your Growth Story
  </button>
</Link> 
             
            </motion.button>
          </div>
        </motion.section>

       
        <motion.section
          ref={ref1}
          className="relative py-24 px-8 bg-gradient-to-br from-white to-blue-50 min-h-[70vh] flex items-center overflow-hidden z-10"
        >
          <motion.img
            src="/images/smm-growth-chart-overlay.png" // Abstract growth chart/data visualization
            alt="Growth chart illustration"
            className="absolute inset-0 w-full h-full object-cover opacity-10"
            style={{ y: y1 }}
            loading="lazy"
          />
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center z-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
              variants={slideInLeft}
            >
              <h2 className="text-3xl font-extrabold mb-6 text-blue-700 leading-tight">
                Beyond Clicks: Crafting Connections That Convert.
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                In a crowded digital world, visibility is just the beginning. True success lies in fostering genuine relationships and turning casual browsers into loyal advocates. We don't just post; we strategically cultivate your online presence.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                Unlock the unseen potential of social media – where every interaction is an opportunity for growth, engagement, and direct revenue.
              </p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
              variants={slideInRight}
              className="flex justify-center"
            >
              <img
                src="https://www.oflox.com/blog/wp-content/uploads/2020/07/What-Is-SMM-In-Digital-Marketing.png" // Illustration of interconnected people/social icons
                alt="Social media connections"
                className="w-full max-w-md h-auto shadow-xl rounded-lg"
                loading="lazy"
              />
            </motion.div>
          </div>
        </motion.section>

        {/* 3. The Transformation Arc: Before & After (New Layout) */}
        {/* <motion.section
          ref={ref2}
          className="relative py-24 px-8 bg-white text-white min-h-[600px] flex items-center z-10 overflow-hidden"
        >
          <motion.img
            src="https://images.unsplash.com/photo-1510519154314-5f5439a9c3d4?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" // Abstract network pattern, blurred/desaturated
            alt="Abstract network background"
            className="absolute inset-0 w-full h-full object-cover opacity-10"
            style={{ y: y2 }}
            loading="lazy"
          />
          <div className="max-w-7xl mx-auto z-10 w-full">
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold text-center mb-16 leading-tight drop-shadow-lg text-orange-500"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeIn}
            >
              Your Brand's Digital Transformation
            </motion.h2>

           
            <div className="grid md:grid-cols-2 gap-12 items-start">
             
              <motion.div
                className="flex flex-col items-center text-center p-8 bg-white/10 rounded-xl shadow-2xl backdrop-blur-md border border-white/20 transform hover:-translate-y-2 transition-transform duration-300 h-full" 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                variants={slideInLeft}
              >
                <h3 className="text-4xl font-extrabold mb-6 text-orange-400">Before</h3>
                <i className="fas fa-exclamation-triangle text-7xl text-gray-800 mb-6 opacity-90"></i> 
                <h4 className="text-3xl font-bold mb-4">Lost & Unheard</h4> 
                <p className="text-lg opacity-80 leading-relaxed">
                  Stagnant engagement, missed opportunities, and a silent online presence. Your brand wasn't reaching its full potential.
                </p>
              </motion.div>

              
              <motion.div
                className="flex flex-col items-center text-center p-8 bg-white/10 rounded-xl shadow-2xl backdrop-blur-md border border-white/20 transform hover:-translate-y-2 transition-transform duration-300 h-full"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                variants={slideInRight}
              >
                <h3 className="text-4xl font-extrabold mb-6 text-orange-400">After</h3> 
                <i className="fas fa-chart-line text-7xl text-white mb-6 opacity-90"></i>
                <h4 className="text-3xl font-bold mb-4">Resonating & Growing</h4> 
                <p className="text-lg opacity-80 leading-relaxed">
                  Strategic content, amplified reach, engaged communities, and measurable ROI. See your brand thrive online.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section> */}

        
        <motion.section
          ref={ref3}
          className="relative py-24 px-8 bg-white min-h-[50vh] flex items-center overflow-hidden z-10"
        >
         
          <div className="absolute inset-0 z-0 pointer-events-none">
            <div className="absolute top-[20%] left-[5%] w-[80px] h-[80px] bg-blue-200 rounded-full opacity-30 animate-float-slow-small"></div>
            <div className="absolute bottom-[10%] right-[15%] w-[120px] h-[120px] bg-orange-200 rounded-full opacity-30 animate-float-slow-large"></div>
          </div>

          <div className="max-w-6xl mx-auto text-center z-10">
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold mb-16 text-blue-800 leading-tight"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeIn}
            >
              Our Growth Levers: What We Do Differently
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
              {[
                { icon: "fas fa-brain", title: "Intelligent Strategy", description: "Data-backed roadmaps for your unique brand." },
                { icon: "fas fa-camera-retro", title: "Creative Storytelling", description: "Visuals and copy that stop the scroll." },
                { icon: "fas fa-bullseye", title: "Precision Paid Ads", description: "Targeted campaigns for maximum ROI." },
                { icon: "fas fa-comments", title: "Authentic Engagement", description: "Building loyal communities, not just followers." },
                { icon: "fas fa-lightbulb", title: "Continuous Innovation", description: "Staying ahead of trends, always optimizing." },
                { icon: "fas fa-chart-pie", title: "Transparent Analytics", description: "Clear insights into your performance." },
              ].map((lever, index) => (
                <motion.div
                  key={index}
                  className="flex flex-col items-center justify-center p-8 rounded-xl shadow-xl bg-blue-950 text-white transform hover:-translate-y-2 transition-transform duration-300 relative overflow-hidden group"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  variants={scaleIn}
                  transition={{ delay: 0.1 * index }}
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-xl"></div>
                  <i className={`${lever.icon} text-7xl mb-6 relative z-10 group-hover:text-orange-300 transition-colors duration-300`}></i>
                  <h3 className="text-2xl font-bold mb-3 relative z-10 text-orange-600">{lever.title}</h3>
                  <p className="text-lg opacity-80 relative z-10">{lever.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section> 

        {/* 5. Action: Let's Connect & Ignite */}
        {/* <motion.section
          ref={ref5}
          className="relative py-24 px-8 bg-gradient-to-br from-white to-blue-100 min-h-[50vh] flex flex-col items-center justify-center text-center z-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeIn}
        >
          <h2 className="text-4xl md:text-5xl font-extrabold mb-8 text-blue-700 leading-tight">
            Ready to Redefine Your Social Story?
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mb-12 leading-relaxed">
            Stop guessing, start growing. Let's craft an innovative SMM strategy that puts your brand in the spotlight and drives the results you deserve.
          </p>
          <motion.button
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-full shadow-2xl transform transition duration-300 ease-in-out hover:scale-105 text-xl flex items-center justify-center"
            whileHover={{ scale: 1.05, boxShadow: '0 12px 30px rgba(0,0,0,0.4)' }}
            whileTap={{ scale: 0.95 }}
            aria-label="Get a custom SMM proposal"
          >
            <i className="fas fa-paper-plane mr-4"></i> Get a Custom Proposal
          </motion.button>
        </motion.section> */}

      </div>

      {/* CSS for custom animations (add to your global CSS or a dedicated component CSS file) */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-10px) translateX(5px); }
          100% { transform: translateY(0px) translateX(0px); }
        }
        @keyframes float-delay {
          0% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(10px) translateX(-5px); }
          100% { transform: translateY(0px) translateX(0px); }
        }
        @keyframes float-slow {
          0% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-7px) translateX(7px); }
          100% { transform: translateY(0px) translateX(0px); }
        }
        @keyframes float-slow-small {
            0% { transform: translateY(0px) translateX(0px); }
            50% { transform: translateY(5px) translateX(-5px); }
            100% { transform: translateY(0px) translateX(0px); }
        }
        @keyframes float-slow-large {
            0% { transform: translateY(0px) translateX(0px); }
            50% { transform: translateY(-8px) translateX(8px); }
            100% { transform: translateY(0px) translateX(0px); }
        }

        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes pulse {
            0%, 100% {
                opacity: 1;
            }
            50% {
                opacity: .5;
            }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delay {
          animation: float-delay 7s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-float-slow-small {
            animation: float-slow-small 5s ease-in-out infinite;
        }
        .animate-float-slow-large {
            animation: float-slow-large 9s ease-in-out infinite;
        }
        .animate-bounce-slow {
            animation: bounce-slow 3s ease-in-out infinite;
        }
        .animate-pulse-slow {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </>
  );
};

export default SMMPage;